import { PDFDocument, rgb } from 'pdf-lib';
import * as libCon from '../Constants.js';
// import { getDocument } from 'pdfjs-dist';
import * as pdfjsLib from 'pdfjs-dist/webpack.mjs';
import {
  filterObjectByFunction,
  getAllIndexes,
  isNullOrUndefined,
  isNullOrUndefinedOrEmpty,
  isNumeric,
  isValidDate,
  stringIsNumeric,
} from './generalFunctions.js';
import { parse } from 'date-fns';
import JSZip from 'jszip';
import jaroWinkler from 'jaro-winkler';
import levenshtein from 'fast-levenshtein';
import { decryptPdf } from './serverFunctions.js';

function normalizeNumber(phone) {
  return phone.replace(/\D/g, ''); // Remove all non-digit characters
}

export function phoneMatchScore(num1, num2) {
  const n1 = normalizeNumber(num1);
  const n2 = normalizeNumber(num2);
  const maxLength = Math.max(n1.length, n2.length);

  if (maxLength === 0) return 1;

  const distance = levenshtein.get(n1, n2);
  return (maxLength - distance) / maxLength;
}

export function ageDiff(age1, age2) {
  if (isNullOrUndefined(age1) || !isNumeric(age1) || isNullOrUndefined(age2) || !isNumeric(age2)) return Infinity;

  return Math.abs(age1 - age2);
}

export const ageScoreFromDiff = (diff) => {
  if (isNullOrUndefined(diff) || !isFinite(diff)) return 0;

  return Math.max(0, 1 - diff * 0.05);
};

export function namesMatchScore(name1, name2) {
  name1 = name1.toLowerCase().replace('ben', '');
  name2 = name2.toLowerCase().replace('ben', '');
  return jaroWinkler(name1, name2);
}

export function sewaIdMatchScore(sewaId1, sewaId2) {
  return jaroWinkler(sewaId1, sewaId2);
}

export function overallScore(meta) {
  let score =
    0.5 * meta[libCon.ATF_SEWA_ID_SCORE] +
    0.3 * meta[libCon.ATF_NAME_SCORE] +
    0.1 * meta[libCon.ATF_PHONE_SCORE] +
    0.1 * ageScoreFromDiff(meta[libCon.ATF_AGE_DIFF]);
  return Math.round(score * 100) / 100;
}

export function getMatches(metadata, participants, location = null) {
  // Filters by city
  if (!isNullOrUndefined(location)) {
    participants = filterObjectByFunction(
      participants,
      (p) => !isNullOrUndefinedOrEmpty(p[libCon.ATF_AREA]) && p[libCon.ATF_AREA][0] === location
    );
  }

  let processedArray = Object.values(participants).map((p) => {
    let values = {};

    // Location
    // -------------
    values[libCon.ATF_LOCATION] = p[libCon.ATF_AREA];

    // Name
    // --------------
    values[libCon.ATF_TARGET_COMPLETE_NAME] = `${p[libCon.ATF_NAME]} ${p[libCon.ATF_LAST_NAME]}`;
    // Score
    let score = 0;
    if (!isNullOrUndefined(metadata[libCon.ATF_NAME]) && !isNullOrUndefined(values[libCon.ATF_TARGET_COMPLETE_NAME]))
      score = namesMatchScore(values[libCon.ATF_TARGET_COMPLETE_NAME], metadata[libCon.ATF_NAME]);

    values[libCon.ATF_NAME_SCORE] = Math.round(score * 100) / 100;

    // Sewa Id
    // --------------
    score = 0;
    values[libCon.ATF_TARGET_SEWA_ID] = p[libCon.ATF_SEWA_ID];

    if (!isNullOrUndefined(metadata[libCon.ATF_SEWA_ID]) && !isNullOrUndefined(values[libCon.ATF_TARGET_SEWA_ID]))
      score = sewaIdMatchScore(values[libCon.ATF_TARGET_SEWA_ID], metadata[libCon.ATF_SEWA_ID]);

    values[libCon.ATF_SEWA_ID_SCORE] = Math.round(score * 100) / 100;

    // Phone Number
    // --------------
    values[libCon.ATF_TARGET_PHONE_NUMBER] = null;
    if (libCon.ATF_PHONE_NUMBER in p)
      values[libCon.ATF_TARGET_PHONE_NUMBER] = p[libCon.ATF_PHONE_NUMBER].replace(/[()\-\s]/g, '');

    // Checks match
    values[libCon.ATF_PHONE_SCORE] = 0;
    if (
      !isNullOrUndefined(metadata[libCon.ATF_PHONE_NUMBER]) &&
      !isNullOrUndefined(values[libCon.ATF_TARGET_PHONE_NUMBER]) &&
      values[libCon.ATF_TARGET_PHONE_NUMBER] !== '0000000000'
    )
      values[libCon.ATF_PHONE_SCORE] = phoneMatchScore(
        `${values[libCon.ATF_TARGET_PHONE_NUMBER]}`,
        `${metadata[libCon.ATF_PHONE_NUMBER]}`
      );

    // Age
    // --------------
    values[libCon.ATF_TARGET_AGE] = null;
    if (libCon.ATF_AGE in p) values[libCon.ATF_TARGET_AGE] = p[libCon.ATF_AGE];

    // Gets the age score / diff
    values[libCon.ATF_AGE_DIFF] = Infinity;
    if (!isNullOrUndefined(metadata[libCon.ATF_AGE]) && !isNullOrUndefined(values[libCon.ATF_TARGET_AGE]))
      values[libCon.ATF_AGE_DIFF] = ageDiff(values[libCon.ATF_TARGET_AGE], metadata[libCon.ATF_AGE]);

    // Confident Mathch
    values[libCon.ATF_SCORE] = overallScore(values);

    return values;
  });

  processedArray = processedArray.sort((a, b) => b[libCon.ATF_SCORE] - a[libCon.ATF_SCORE]);

  // Adds second best score
  processedArray = processedArray.map((p, i) => {
    return {
      ...p,
      [libCon.ATF_SCORE_SECOND_BEST]: i + 1 >= processedArray.length ? null : processedArray[i + 1][libCon.ATF_SCORE],
      [libCon.ATF_NAME_SCORE_SECOND_BEST]:
        i + 1 >= processedArray.length ? null : processedArray[i + 1][libCon.ATF_NAME_SCORE],
      [libCon.ATF_NAME_SECOND_BEST]:
        i + 1 >= processedArray.length ? null : processedArray[i + 1][libCon.ATF_TARGET_COMPLETE_NAME],
      [libCon.ATF_PHONE_SCORE_SECOND_BEST]:
        i + 1 >= processedArray.length ? null : processedArray[i + 1][libCon.ATF_PHONE_SCORE],
      [libCon.ATF_AGE_DIFF_SECOND_BEST]:
        i + 1 >= processedArray.length ? null : processedArray[i + 1][libCon.ATF_AGE_DIFF],
    };
  });
  return processedArray;
}

export async function extractPDFsFromZip(zipFileBuffer, setProcessProgress = (i) => true) {
  const zip = await JSZip.loadAsync(zipFileBuffer); // Load the ZIP file
  const pdfFiles = [];
  const metadataArray = [];
  const errorFiles = [];

  // Iterate through all files in the ZIP
  let i = 0;
  for (const fileName of Object.keys(zip.files)) {
    try {
      if (fileName.endsWith('/')) continue;

      const pdfBlob = await zip.files[fileName].async('arraybuffer'); // Convert to Blob
      const pdfBytes = new Uint8Array(pdfBlob);

      let metadata = extractMetadata(await extractAllTextWithCoordinates(new Uint8Array(pdfBytes)));

      // Checks if meteadata is complete
      const necessary_ids = [libCon.ATF_CASE_ID, libCon.ATF_SAMPLE_DATE];
      if (necessary_ids.some((k) => isNullOrUndefined(metadata[k])))
        throw new Error('Missing necessary metadata values');

      metadata[libCon.ATF_FILE_NAME] = fileName.split('/').pop();

      pdfFiles.push(new Uint8Array(pdfBytes));
      metadataArray.push(metadata);
    } catch (error) {
      errorFiles.push(fileName.split('/').pop());
    }

    i += 1;
    setProcessProgress((100 * i) / Object.keys(zip.files).length);
  }

  return [pdfFiles, metadataArray, errorFiles];
}

export async function splitPdf(fileBytes, progressFunction = (prog) => true) {
  try {
    const originalBuffer = fileBytes;
    const bufferForPdfLib = originalBuffer.slice(0);
    const bufferForPdfjs = originalBuffer.slice(0);

    // Load documents
    const pdfLibDoc = await PDFDocument.load(bufferForPdfLib);
    const pdfDocument = await pdfjsLib.getDocument({ data: bufferForPdfjs }).promise;

    const numPages = pdfDocument.numPages;

    const zip = new JSZip();
    let currentDoc = await PDFDocument.create();
    let totalReports = 0;

    // Loop through each page
    for (let i = 0; i < numPages; i++) {
      // Use pdf-lib to copy the page
      const [copiedPage] = await currentDoc.copyPages(pdfLibDoc, [i]);
      currentDoc.addPage(copiedPage);

      // Use pdfjs-dist to read the page text
      const page = await pdfDocument.getPage(i + 1); // pdfjs uses 1-based indexing
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item) => item.str).join(' ');

      // console.log(pageText);

      // Checks if it has x of x
      const match = pageText.match(/Page\s+(\d+)\s+of\s+(\d+)/);
      const isEndPage = match && match[1] === match[2];

      if (isEndPage || i === numPages - 1) {
        const pdfBytes = await currentDoc.save();
        let metadata = extractMetadata(await extractAllTextWithCoordinates(pdfBytes.slice(0))); // Sends a Copy
        zip.file(`${metadata[libCon.ATF_CASE_ID]}.pdf`, pdfBytes);
        totalReports++;
        currentDoc = await PDFDocument.create();
      }

      progressFunction((100 * i) / numPages);
    }

    // Generate and download zip
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(zipBlob);
    link.download = 'all_reports.zip';
    link.click();

    return totalReports;
  } catch (error) {
    console.error('Error splitting PDF:', error);
    throw error;
  }
}

export async function extractAllTextWithCoordinates(pdfBytes) {
  try {
    const pdfDocument = await pdfjsLib.getDocument({ data: pdfBytes }).promise;

    let allTextWithCoords = [];

    for (let i = 1; i <= pdfDocument.numPages; i++) {
      const page = await pdfDocument.getPage(i);
      const textContent = await page.getTextContent();

      const pageTextWithCoords = textContent.items
        .map((item) => ({
          str: item.str,
          x: item.transform[4],
          y: item.transform[5],
        }))
        .filter((item) => item.str.trim() !== '');

      allTextWithCoords = allTextWithCoords.concat(pageTextWithCoords);
    }

    return allTextWithCoords;
  } catch (error) {
    console.error('Error extracting text from PDF:', error);
    throw error;
  }
}

export function getLabByText(textCoordinatesArray) {
  // Checks for Neuberg
  if (textCoordinatesArray.some((ob) => ob.str.replace(' ', '').toUpperCase() === 'LABORATORYREPORT'))
    return libCon.NEUBERG;

  // Checks for Pathlabs
  if (textCoordinatesArray.some((ob) => ob.str === 'Report Status')) return libCon.PATH_LABS;

  throw new Error('Could not identify the lab type');
}

export function extractMetadata(textCoordinatesArray) {
  let labType = getLabByText(textCoordinatesArray);
  let response = null;

  switch (labType) {
    case libCon.NEUBERG:
      response = extractMetadataNeuberg(textCoordinatesArray);
      break;
    case libCon.PATH_LABS:
      response = extractMetadataPathLabs(textCoordinatesArray);
      break;
    default:
      throw new Error(`No implementation for labType: ${labType}`);
  }

  response[libCon.ATF_LAB] = labType;

  return response;
}

export function extractMetadataPathLabs(textCoordinatesArray) {
  let epsylon = 1.8;
  let lineDifference = 30;

  let key;
  let validationFunction = (_) => true;
  let multipleLines = false;
  let lineConcatenation = ' ';
  let max_x_diff = null;

  let response = {};

  // Sewa Id
  key = 'Name';
  multipleLines = false;
  lineConcatenation = '';
  validationFunction = (val) => true;

  let sewaId = getValueInsideCoordinateValues(
    textCoordinatesArray,
    key,
    epsylon,
    validationFunction,
    multipleLines,
    lineConcatenation,
    lineDifference
  );

  if (!isNullOrUndefined(sewaId)) {
    sewaId = sewaId.replace(/[^0-9]/g, '').trim();
    // Checks if valid
    let intVal = parseInt(sewaId);
    if (!isNumeric(intVal) || !sewaId.startsWith('20') || sewaId.length < 13 || sewaId.length > 15) sewaId = null;
  }

  response[libCon.ATF_SEWA_ID] = sewaId;

  // Name
  key = 'Name';
  multipleLines = false;
  lineConcatenation = ' ';
  validationFunction = (_) => true;

  let name = getValueInsideCoordinateValues(
    textCoordinatesArray,
    key,
    epsylon,
    validationFunction,
    multipleLines,
    lineConcatenation,
    lineDifference
  );

  if (!isNullOrUndefined(name)) {
    name = name.replace('Mrs', '');
    name = name.replace('Ms', '');

    // Remove numbers and dots
    name = name.replace(/[0-9.]/g, '').trim();
  }

  response[libCon.ATF_NAME] = name;

  // CaseId
  key = 'Lab No.';
  multipleLines = false;
  lineConcatenation = ' ';
  validationFunction = (val) => stringIsNumeric(val);

  let caseId = getValueInsideCoordinateValues(
    textCoordinatesArray,
    key,
    epsylon,
    validationFunction,
    multipleLines,
    lineConcatenation,
    lineDifference
  );

  response[libCon.ATF_CASE_ID] = caseId;

  // Age
  key = 'Age';
  multipleLines = false;
  lineConcatenation = ' ';
  validationFunction = (val) => {
    val = val.replace('Years', '').trim();
    return stringIsNumeric(val);
  };

  let age = getValueInsideCoordinateValues(
    textCoordinatesArray,
    key,
    epsylon,
    validationFunction,
    multipleLines,
    lineConcatenation,
    lineDifference
  );

  if (!isNullOrUndefined(age)) age = parseInt(age.replace('Years', '').trim());

  response[libCon.ATF_AGE] = age;

  // Phone Number
  response[libCon.ATF_PHONE_NUMBER] = null;

  // Sample Date
  key = 'Collected';
  multipleLines = false;
  lineConcatenation = ' ';
  max_x_diff = 150;
  validationFunction = (val) => {
    return isValidDate(parse(val, 'd/M/yyyy h:mm:ssa', new Date()));
  };

  let sampleDate = getValueInsideCoordinateValues(
    textCoordinatesArray,
    key,
    epsylon,
    validationFunction,
    multipleLines,
    lineConcatenation,
    lineDifference,
    max_x_diff
  );

  // If null tries two step
  if (isNullOrUndefined(sampleDate)) {
    validationFunction = (val) => {
      return isValidDate(parse(val, 'd/M/yyyy', new Date()));
    };

    let datePart = getValueInsideCoordinateValues(
      textCoordinatesArray,
      key,
      epsylon,
      validationFunction,
      multipleLines,
      lineConcatenation,
      lineDifference,
      max_x_diff
    );

    validationFunction = (val) => {
      return isValidDate(parse(val, 'h:mm:ssa', new Date()));
    };

    let timePart = getValueInsideCoordinateValues(
      textCoordinatesArray,
      key,
      epsylon,
      validationFunction,
      multipleLines,
      lineConcatenation,
      lineDifference,
      max_x_diff
    );

    if (!isNullOrUndefined(datePart) && !isNullOrUndefined(timePart)) sampleDate = `${datePart} ${timePart}`;
  }

  if (!isNullOrUndefined(sampleDate)) sampleDate = parse(sampleDate, 'd/M/yyyy h:mm:ssa', new Date());

  response[libCon.ATF_SAMPLE_DATE] = sampleDate;

  return response;
}

export function extractMetadataNeuberg(textCoordinatesArray) {
  let epsylon = 1.5;
  let lineDifference = 30;

  let key;
  let validationFunction = (_) => true;
  let multipleLines = false;
  let lineConcatenation = ' ';

  let response = {};

  // Sewa Id
  key = 'Ref Id1';
  multipleLines = true;
  lineConcatenation = '';
  validationFunction = (val) => {
    let intVal = parseInt(val);
    return isNumeric(intVal) && val.startsWith('20') && val.length >= 13 && val.length <= 15;
  };

  let sewaId = getValueInsideCoordinateValues(
    textCoordinatesArray,
    key,
    epsylon,
    validationFunction,
    multipleLines,
    lineConcatenation,
    lineDifference
  );

  // If null, retries with Ref Id2
  if (isNullOrUndefined(sewaId)) {
    key = 'Ref Id2';
    sewaId = getValueInsideCoordinateValues(
      textCoordinatesArray,
      key,
      epsylon,
      validationFunction,
      multipleLines,
      lineConcatenation,
      lineDifference
    );
  }

  response[libCon.ATF_SEWA_ID] = sewaId;

  // Name
  key = 'Name';
  multipleLines = true;
  lineConcatenation = ' ';
  validationFunction = (val) => true;

  let name = getValueInsideCoordinateValues(
    textCoordinatesArray,
    key,
    epsylon,
    validationFunction,
    multipleLines,
    lineConcatenation,
    lineDifference
  );

  if (!isNullOrUndefined(name)) {
    name = name.replace('Mrs', '');
    name = name.replace('Ms', '');

    // Remove numbers and dots
    name = name.replace(/[0-9.]/g, '').trim();
  }

  response[libCon.ATF_NAME] = name;

  // CaseId
  key = 'Case ID';
  multipleLines = false;
  lineConcatenation = ' ';
  validationFunction = (val) => isNumeric(parseInt(val));

  let caseId = getValueInsideCoordinateValues(
    textCoordinatesArray,
    key,
    epsylon,
    validationFunction,
    multipleLines,
    lineConcatenation,
    lineDifference
  );

  response[libCon.ATF_CASE_ID] = caseId;

  // Age
  key = 'Sex/Age';
  multipleLines = false;
  lineConcatenation = ' ';
  validationFunction = (val) => {
    val = val.replace('Years', '').trim();
    return isNumeric(parseInt(val));
  };

  let age = getValueInsideCoordinateValues(
    textCoordinatesArray,
    key,
    epsylon,
    validationFunction,
    multipleLines,
    lineConcatenation,
    lineDifference
  );

  if (!isNullOrUndefined(age)) age = parseInt(age.replace('Years', '').trim());

  response[libCon.ATF_AGE] = age;

  // Phone Number
  key = 'Mobile No.';
  multipleLines = false;
  lineConcatenation = ' ';
  validationFunction = (val) => isNumeric(parseInt(val));

  let phoneNumber = getValueInsideCoordinateValues(
    textCoordinatesArray,
    key,
    epsylon,
    validationFunction,
    multipleLines,
    lineConcatenation,
    lineDifference
  );

  response[libCon.ATF_PHONE_NUMBER] = phoneNumber;

  // Sample Date
  key = 'Sample Date and Time';
  multipleLines = false;
  lineConcatenation = ' ';
  validationFunction = (val) => {
    return isValidDate(parse(val, 'dd-MMM-yyyy HH:mm', new Date()));
  };

  let sampleDate = getValueInsideCoordinateValues(
    textCoordinatesArray,
    key,
    epsylon,
    validationFunction,
    multipleLines,
    lineConcatenation,
    lineDifference
  );

  if (!isNullOrUndefined(sampleDate)) sampleDate = parse(sampleDate, 'dd-MMM-yyyy HH:mm', new Date());

  response[libCon.ATF_SAMPLE_DATE] = sampleDate;

  return response;
}

export function getValueInsideCoordinateValues(
  textCoordinatesArray,
  key,
  epsylon,
  validationFunction = (_) => true,
  multipleLines = false,
  lineConcatenation = ' ',
  lineDifference = 1,
  max_x_diff = null
) {
  try {
    let textArray = textCoordinatesArray.map((ob) => ob.str);

    let keyIndex = -1;
    let matches, matches2;

    // Key Index
    keyIndex = textArray.indexOf(key);

    if (keyIndex > -1) {
      // Gets the coordinates
      let x = textCoordinatesArray[keyIndex].x;
      let y = textCoordinatesArray[keyIndex].y;

      // Filters for matching rows
      matches = textCoordinatesArray.filter(
        (ob) =>
          Math.abs(ob.y - y) <= epsylon &&
          ob.x > x &&
          ob.str !== ':' &&
          ob.str !== '/' &&
          ob.str.replace(' ', '') !== '' &&
          (max_x_diff === null || ob.x - x < max_x_diff)
      );

      for (let j in matches) {
        let matchedString = matches[j].str;

        if (multipleLines) {
          // Searches for the missing part
          matches2 = textCoordinatesArray.filter((ob) => {
            // Same column
            if (Math.abs(ob.x - matches[j].x) > epsylon) return false;
            // Next Row
            if (ob.y > matches[j].y || Math.abs(ob.y - matches[j].y) > lineDifference) return false;
            // Not same value
            if (matchedString === ob.str) return false;

            let newMatchedString = `${matchedString}${lineConcatenation}${ob.str}`;
            if (!validationFunction(newMatchedString)) return false;

            return true;
          });

          if (matches2.length > 0) matchedString = `${matchedString}${lineConcatenation}${matches2[0].str}`;
        }

        if (validationFunction(matchedString)) return matchedString;
      }
    }

    return null;
  } catch (error) {
    return null;
  }
}

export async function isEncrypted(pdfBytes) {
  try {
    await PDFDocument.load(pdfBytes);
    return false; // no error → not encrypted
  } catch (err) {
    if (err.message.includes('encrypted') || err.toString().includes('encrypted')) {
      return true;
    }
    throw err; // some other parsing error
  }
}

export async function processPdf(labType, pdfBytes, replacement) {
  switch (labType) {
    case libCon.NEUBERG:
      return processPdfNeubergSupratech(pdfBytes, replacement);
    case libCon.PATH_LABS:
      return processPdfPathLabs(pdfBytes, replacement);
    default:
      throw new Error(`No implementation for masking labType: ${labType}`);
  }
}

export async function processPdfPathLabs(pdfBytes, replacement) {
  // Checks for encryption
  if (isEncrypted(pdfBytes)) {
    pdfBytes = await decryptPdf(pdfBytes);
  }

  const pdfDoc = await PDFDocument.load(pdfBytes);
  const pages = pdfDoc.getPages();

  let key = 'Name';
  let epsylon = 1.8;

  await Promise.all(
    pages.map(async (page, i) => {
      // Converts page into stand alone pdf
      // Create a new empty PDF
      const newPdf = await PDFDocument.create();

      // Copy one page from the original PDF
      const [copiedPage] = await newPdf.copyPages(pdfDoc, [i]);
      newPdf.addPage(copiedPage);

      // Converts into text with coordinates
      const textCoordinatesArray = await extractAllTextWithCoordinates(await newPdf.save());

      let nameOnFile = getValueInsideCoordinateValues(textCoordinatesArray, key, epsylon);

      let nameObject = textCoordinatesArray.find((ob) => ob.str === nameOnFile);

      // Gets the coordinates
      if (isNullOrUndefined(nameOnFile) || isNullOrUndefined(nameObject)) return;

      let x = nameObject.x;
      let y = nameObject.y;

      // Name
      // Rectangle
      page.drawRectangle({
        x: x - 1,
        y: y - 2,
        width: 480,
        height: 15,
        color: rgb(1, 1, 1), // White color
      });

      // Overlay text
      page.drawText(libCon.REPORT_NAME_MASK, {
        x: x,
        y: y,
        size: 11, // Adjust the font size as needed
        color: rgb(1, 1, 1), // Black color
      });

      page.drawText(replacement.toString(), {
        x: x,
        y: y,
        size: 11, // Adjust the font size as needed
        color: rgb(0, 0, 0), // Black color
      });
    })
  );

  // Step 3: Save the modified PDF
  const modifiedPdfBytes = await pdfDoc.save();
  return modifiedPdfBytes;
}

export async function processPdfNeubergSupratech(pdfBytes, replacement) {
  // Checks for encryption
  if (isEncrypted(pdfBytes)) {
    pdfBytes = await decryptPdf(pdfBytes);
  }

  const pdfDoc = await PDFDocument.load(pdfBytes);
  const pages = pdfDoc.getPages();

  let keys = ['Name', 'Name:'];
  let epsylon = 1.8;

  await Promise.all(
    pages.map(async (page, i) => {
      // Converts page into stand alone pdf
      // Create a new empty PDF
      const newPdf = await PDFDocument.create();

      // Copy one page from the original PDF
      const [copiedPage] = await newPdf.copyPages(pdfDoc, [i]);
      newPdf.addPage(copiedPage);

      // Converts into text with coordinates
      const textCoordinatesArray = await extractAllTextWithCoordinates(await newPdf.save());

      let nameOnFile = null;
      for (let key of keys) {
        nameOnFile = getValueInsideCoordinateValues(textCoordinatesArray, key, epsylon);
        if (!isNullOrUndefined(nameOnFile)) break;
      }

      let nameObject = textCoordinatesArray.find((ob) => ob.str === nameOnFile);

      // Gets the coordinates
      if (isNullOrUndefined(nameOnFile) || isNullOrUndefined(nameObject)) return;

      let x = nameObject.x;
      let y = nameObject.y;

      // Name
      // Rectangle
      page.drawRectangle({
        x: x - 1,
        y: y - 14,
        width: 174,
        height: 34,
        color: rgb(1, 1, 1), // White color
      });

      // Overlay text
      page.drawText(libCon.REPORT_NAME_MASK, {
        x: x,
        y: y,
        size: 11, // Adjust the font size as needed
        color: rgb(1, 1, 1), // Black color
      });

      page.drawText(replacement.toString(), {
        x: x,
        y: y,
        size: 11, // Adjust the font size as needed
        color: rgb(0, 0, 0), // Black color
      });
    })
  );

  // Step 3: Save the modified PDF
  const modifiedPdfBytes = await pdfDoc.save();
  return modifiedPdfBytes;

  // // Step 2: Modify the PDF with pdf-lib
  // const pdfDoc = await PDFDocument.load(pdfBytes);
  // const pages = pdfDoc.getPages();

  // pages.forEach((page) => {

  //     // Name
  //     // Rectangle
  //     page.drawRectangle({
  //         x: 96,
  //         y: 700,
  //         width: 174,
  //         height: 34,
  //         color: rgb(1, 1, 1), // White color
  //     });

  //     // Overlay text
  //     page.drawText(libCon.REPORT_NAME_MASK, {
  //         x: 100,
  //         y: 723,
  //         size: 13, // Adjust the font size as needed
  //         color: rgb(1, 1, 1), // Black color
  //     });

  //     page.drawText(replacement.toString(), {
  //         x: 100,
  //         y: 723,
  //         size: 13, // Adjust the font size as needed
  //         color: rgb(0, 0, 0), // Black color
  //     });

  //     // Phone Number
  //     page.drawRectangle({
  //         x: 502,
  //         y: 673,
  //         width: 80,
  //         height: 14,
  //         color: rgb(1, 1, 1), // White color
  //     });
  //     // Overlay text
  //     page.drawText(libCon.REPORT_PHONE_MASK, {
  //         x: 502,
  //         y: 676,
  //         size: 13, // Adjust the font size as needed
  //         color: rgb(1, 1, 1), // Black color
  //     });

  // });

  // // Step 3: Save the modified PDF
  // const modifiedPdfBytes = await pdfDoc.save();
  // return modifiedPdfBytes;
}

export function downloadZip(pdfs, fileNames, zipFileName = 'files.zip') {
  if (pdfs.length !== fileNames.length) {
    console.error('PDFs array and file names array must have the same length.');
    return;
  }

  const zip = new JSZip();

  pdfs.forEach((pdf, index) => {
    zip.file(fileNames[index], pdf, { binary: true });
  });

  zip
    .generateAsync({ type: 'blob' })
    .then((zipBlob) => {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = zipFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    })
    .catch((err) => console.error('Error generating ZIP:', err));
}

// Legacy

export function legacy_extractMetadataNeubergSupratech(textArray) {
  let i = -1;
  let response = {};

  // Sewa Id
  let sewaId = null;
  let indices = getAllIndexes(
    textArray.map((t) => t.replace(' ', '')),
    'MobileNo.'
  );

  // Value might be in second or third page
  for (let j = 0; j < indices.length; j++) {
    let i = indices[j];
    // Colon
    if (i + 1 < textArray.length && textArray[i + 1] === ':') i += 1;

    // Tries the two next lines
    for (let k = 1; k <= 2; k++) {
      if (sewaId === null && i + k < textArray.length) {
        let parsedSewaId = `${textArray[i + k].trim()}${textArray[i + k + 1].trim()}`;
        let numericSewaId = parseInt(parsedSewaId);
        if (!isNullOrUndefined(numericSewaId) && parsedSewaId.length >= 13 && parsedSewaId.startsWith('20')) {
          sewaId = parsedSewaId;
          break;
        }
      }
    }

    if (!isNullOrUndefined(sewaId)) break;
  }
  response[libCon.ATF_SEWA_ID] = sewaId;

  // Name
  let name = null;
  i = textArray.indexOf('Name');
  if (i >= 0) {
    // Colon
    if (i + 1 < textArray.length && textArray[i + 1] === ':') i += 1;

    i += 1;
    name = '';
    while (i < textArray.length && textArray[i] !== 'Sex/Age') {
      name = `${name} ${textArray[i]}`;
      i += 1;
    }
    name = name.trim();

    // Cleans
    name = name.replace('Mrs', '');
  }
  response[libCon.ATF_NAME] = name;

  // CaseId
  let caseId = null;
  i = textArray.indexOf('CaseID');
  if (i >= 0) {
    // Colon
    if (i + 1 < textArray.length && textArray[i + 1] === ':') i += 1;

    if (i + 1 < textArray.length) caseId = textArray[i + 1];
  }

  response[libCon.ATF_CASE_ID] = caseId;

  // Age
  let age = null;
  i = textArray.indexOf('Sex/Age');
  if (i >= 0) {
    i += 3;
    if (i + 1 < textArray.length) {
      age = textArray[i + 1];
      age = parseInt(age.replace('Years', ''));

      if (isNullOrUndefined(age)) age = null;
    }
  }
  response[libCon.ATF_AGE] = age;

  // Phone Number
  let phoneNumber = null;
  indices = getAllIndexes(
    textArray.map((t) => t.replace(' ', '')),
    'MobileNo.'
  );
  // Value might be in second or third page
  for (let j = 0; j < indices.length; j++) {
    let i = indices[j];
    // Colon
    if (i + 1 < textArray.length && textArray[i + 1] === ':') i += 1;

    if (i + 1 < textArray.length) {
      let parsedPhoneNumber = parseInt(textArray[i + 1]);
      if (!isNullOrUndefined(parsedPhoneNumber) && `${parsedPhoneNumber}`.length >= 10) {
        phoneNumber = parsedPhoneNumber;
        break;
      }
    }
  }
  response[libCon.ATF_PHONE_NUMBER] = phoneNumber;

  // Sample Date
  let sampleDate = null;
  indices = getAllIndexes(textArray, 'RefId2');

  // Value might be in second or third page
  for (let j = 0; j < indices.length; j++) {
    let i = indices[j];
    // Colon
    if (i + 1 < textArray.length && textArray[i + 1] === ':') i += 1;

    if (i + 1 < textArray.length) {
      sampleDate = parse(textArray[i + 1], 'dd-MMM-yyyyHH:mm', new Date());
    }

    if (isValidDate(sampleDate)) break;
  }
  response[libCon.ATF_SAMPLE_DATE] = sampleDate;

  return response;
}
